/**
 * The sandbox (`?sandbox=1`) — the whole ladder, without playing four sessions to reach it.
 *
 * Tiers open on measured rolling accuracy and nothing else (invariant 4), which is correct and also
 * means tier 4 — Conversión, the mechanic that makes this not a flashcard — is roughly a fortnight
 * behind the first launch. That is a real problem for the design, and a separate one from being
 * unable to *look* at it. This solves only the second.
 *
 * Three rules hold it away from anything that matters:
 *
 *  1. **Development only.** Gated on `import.meta.env.DEV`, so the flag does not exist in a built
 *     bundle. A seeded ladder reaching the deployed app would put fabricated attempts into the
 *     learner's real history and quietly corrupt every figure El Tablero reports.
 *  2. **Its own database.** `spanish-game-sandbox`, never `spanish-game`. Playing the sandbox can
 *     no more touch a real review than another app could.
 *  3. **Seeded once.** If the sandbox store already has items, nothing is written — so a sandbox
 *     session persists across reloads and can be played properly rather than resetting underfoot.
 *
 * What it fabricates is a plausible history, not a perfect one: ~90% accuracy, spread over ten days.
 * A flawless one would open the ladder just as well and make every tracker reading a lie of a
 * different kind.
 */

import type { SeedItem } from '../content/schema';
import { toItemRecord } from '../content/schema';
import type { Attempt, ItemRecord } from '../store/schema';
import { KEY, PREFIX } from '../store/schema';
import type { Store } from '../store/store';
import { PROFILE_KEY, type LearnerProfile } from '../store/profile';
import type { ModeId, Tier } from '../state/gamestate';

/** Never `spanish-game`. The one line that keeps a fabricated history out of the real store. */
export const SANDBOX_DB = 'spanish-game-sandbox';

const DAY = 86_400_000;

/**
 * Attempts per tier, and how many of them are correct.
 *
 * `DEFAULT_GATING` opens a tier at 85% over the last 20 qualifying attempts, and refuses to move
 * either way below 12. Sixteen at fifteen correct is 93.75% — comfortably open, visibly not
 * perfect.
 */
export const ATTEMPTS_PER_TIER = 16;
export const CORRECT_PER_TIER = 15;

/** How far back the fabricated history runs. Long enough for the tracker's seven-day floor. */
export const HISTORY_DAYS = 10;

/** A plausible mode for each tier, so per-tier accuracy in the tracker does not read as nonsense. */
const TIER_MODE: Readonly<Record<Tier, ModeId>> = Object.freeze({
  1: 'speech',
  2: 'cloze',
  3: 'conjugate',
  4: 'conversion',
  5: 'sentence',
  6: 'conjugate',
});

/** `TIER_MODE` is total over `Tier`, but an index signature cannot say so under `noUncheckedIndexedAccess`. */
function modeFor(tier: Tier): ModeId {
  return TIER_MODE[tier];
}

/**
 * Whether to run at all.
 *
 * Pure, and separated from everything else, because this is the assertion that matters: no
 * combination of URL flags may reach the sandbox in a production build.
 */
export function sandboxEnabled(context: { dev: boolean; requested: boolean }): boolean {
  return context.dev && context.requested;
}

/** `?sandbox=1`, and nothing looser — `?sandbox=0` and `?sandbox` are both off. */
export function sandboxRequested(params: URLSearchParams): boolean {
  return params.get('sandbox') === '1';
}

/**
 * The fabricated attempt history that opens the ladder.
 *
 * Tiers 1 and 2 are open from the first launch, so gating needs evidence at tiers 2 through 5 —
 * each one is what opens the tier above it. Tier 1 is included anyway: nothing is gated on it, but
 * a tracker showing every tier but Sonido would be a confusing thing to hand someone.
 *
 * Item ids are real, taken from the corpus, so the export and the tracker join against something
 * that exists. Attempts are laid down oldest-first across `HISTORY_DAYS`.
 */
export function ladderAttempts(seeds: readonly SeedItem[], now: number): Attempt[] {
  const attempts: Attempt[] = [];
  const tiers: Tier[] = [1, 2, 3, 4, 5];
  const start = now - HISTORY_DAYS * DAY;

  let n = 0;
  for (const tier of tiers) {
    const pool = seeds.filter((seed) => seed.tier === tier);
    if (pool.length === 0) continue;

    for (let i = 0; i < ATTEMPTS_PER_TIER; i += 1) {
      // Spread across the window rather than bunched: the novelty panel reads by week, and a
      // history that all happened in one hour would draw as a single spike.
      const at = start + Math.floor(((i + 1) / (ATTEMPTS_PER_TIER + 1)) * (HISTORY_DAYS - 1) * DAY) + n * 60_000;
      attempts.push({
        id: `sandbox-${tier}-${i}`,
        itemId: pool[i % pool.length]!.id,
        tier,
        mode: modeFor(tier),
        at,
        correct: i < CORRECT_PER_TIER,
        hinted: false,
        isRepeatWithinRun: false,
        wasDueReview: true,
        latencyMs: 4_000,
        nearMiss: null,
        // Production mode, not hinted, not a repeat — so this is what `advancesInterval` would
        // return, and it is what makes the attempt qualify for gating. Stated rather than cast.
        advancedInterval: true,
        runId: `sandbox-run-${Math.floor(i / 6)}`,
      });
      n += 1;
    }
  }
  return attempts;
}

/**
 * How many items of each tier are made due right now.
 *
 * Without this the first sandbox run is still mostly tier 2, because intake orders candidates by
 * tier and the corpus has 251 tier-2 words waiting. A seeded due queue is what makes the very first
 * run a mixed one — which is the entire point of opening the sandbox.
 */
export const DUE_PER_TIER: Readonly<Record<Tier, number>> = Object.freeze({
  1: 3,
  2: 4,
  3: 4,
  4: 5,
  5: 4,
  // Tier 6 — Tiempo. Empty when the sandbox was written, authored since (`tiempo.json`), so it now
  // has due reviews like every other tier.
  6: 3,
});

/**
 * Records for the whole corpus, with three populations.
 *
 * - **Due now** — `DUE_PER_TIER` of each tier, already introduced, in `review`. These are what the
 *   first run draws, and they are why it is mixed from the first card.
 * - **Parked** — most of tier 2, in `review` but not due for a month. Not busywork and not
 *   candidates either: without this, intake spends every one of its ten daily slots on tier-2
 *   vocabulary and the higher tiers stay invisible behind a queue that is technically unlocked.
 * - **New** — everything else, untouched. This is what keeps the teaching step in play, including
 *   the conversion rules, which is the one thing worth watching land.
 */
export function sandboxItems(seeds: readonly SeedItem[], now: number): ItemRecord[] {
  const dueRemaining: Record<number, number> = { ...DUE_PER_TIER };
  // Tier 2 keeps a working supply of genuinely new words; the rest is parked.
  let tier2New = 30;
  let dueIndex = 0;

  return seeds.map((seed) => {
    const record = toItemRecord(seed, now);

    if ((dueRemaining[seed.tier] ?? 0) > 0) {
      dueRemaining[seed.tier] = (dueRemaining[seed.tier] ?? 0) - 1;
      return due(record, now, dueIndex++);
    }
    if (seed.tier === 2) {
      if (tier2New > 0) {
        tier2New -= 1;
        return record;
      }
      return reviewed(record, now, now + 30 * DAY);
    }
    return record;
  });
}

/**
 * A due review, with a memory unlike its neighbours'.
 *
 * The variation is the point, not decoration. The due queue is ordered by retrievability, ties
 * broken on `dueAt` and then on `id` — so a batch of items sharing one memory falls back to
 * alphabetical order, which groups them by tier, because ids are authored per content file. The
 * first sandbox run drew eight cards from tiers 4 and 5 and nothing else: interleaving was working
 * exactly as written, on a queue that had no spread in it to interleave.
 *
 * Deterministic, from the index. Nothing in this app reads `Math.random` and a development helper
 * is not the place to start.
 */
function due(record: ItemRecord, now: number, index: number): ItemRecord {
  const spread = index % 7;
  return {
    ...record,
    introducedAt: now - (HISTORY_DAYS + spread) * DAY,
    memory: {
      ...record.memory,
      state: 'review',
      // Two to fourteen days of stability, last seen one to seven days ago: a range of ordinary
      // items rather than a cohort that all arrived together.
      stability: 2 + spread * 2,
      difficulty: 4 + (index % 4),
      reps: 2 + (index % 5),
      lapses: index % 3 === 0 ? 1 : 0,
      lastReviewAt: now - (1 + spread) * DAY,
      // All in the past, so all genuinely due, but not to the same minute.
      dueAt: now - (index % 5) * 3_600_000,
    },
  };
}

/** An item the learner has met and answered before: introduced, in `review`, due when told. */
function reviewed(record: ItemRecord, introducedAt: number, dueAt: number): ItemRecord {
  return {
    ...record,
    introducedAt: introducedAt - HISTORY_DAYS * DAY,
    memory: {
      ...record.memory,
      state: 'review',
      // Ordinary middling values. Nothing here should read as an item in trouble or an item mastered.
      stability: 4,
      difficulty: 5,
      reps: 3,
      lapses: 0,
      lastReviewAt: introducedAt - 2 * DAY,
      dueAt,
    },
  };
}

/** A profile ten days old with its baseline answered, so the sandbox opens straight into a run. */
export function sandboxProfile(now: number): LearnerProfile {
  return {
    firstLaunchAt: now - HISTORY_DAYS * DAY,
    baseline: { retentionPct: null, dailyMinutes: null, recordedAt: now - HISTORY_DAYS * DAY },
    // Ten days in, so the conversion demonstration has been and gone. The sandbox exists to show
    // the tiers; the demonstration is what a genuine first launch shows, and it is one tap away at
    // the plain URL.
    demoSeenAt: now - HISTORY_DAYS * DAY,
  };
}

/**
 * Fill an empty sandbox store. Returns whether anything was written.
 *
 * Idempotent by the presence of items, not by a flag: a flag can be written and then the write that
 * mattered can fail, and a half-seeded sandbox is worse than an unseeded one because it looks
 * finished.
 */
export async function seedSandbox(
  store: Store,
  seeds: readonly SeedItem[],
  now: number,
): Promise<boolean> {
  const existing = await store.list<ItemRecord>(PREFIX.item);
  if (existing.length > 0) return false;

  await store.set(PROFILE_KEY, sandboxProfile(now));
  /*
   * Written in parallel, the way `ItemLibrary.load` seeds a fresh corpus.
   *
   * Sequentially awaiting ~490 IndexedDB writes is a visible pause on a phone, and it lands
   * squarely between tapping the link and seeing anything at all — which reads as the app being
   * broken rather than as the app being busy.
   */
  await Promise.all(
    sandboxItems(seeds, now).map((record) => store.set(KEY.item(record.id), record)),
  );
  await Promise.all(
    ladderAttempts(seeds, now).map((attempt) => store.set(KEY.attempt(attempt.id), attempt)),
  );
  return true;
}
